import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, List, ListItemButton, ListItemText, Chip, TextField, Tooltip, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  FLOW_NAME_MAX_LENGTH,
  flowNameLength,
  normalizeFlowName,
  type RuleFlow,
} from '../../../services/channelRuleFlowsService';
import { isFlowIncomplete, isFlowMissingDetail } from './ruleFlowValidation';

export interface RuleFlowRailProps {
  flows: RuleFlow[];
  selectedFlowId: string | null;
  onSelectFlow: (flowId: string) => void;
  /** Flow rename (shay-email-backend#564). Optional on purpose: when it is not passed the rail
   * renders no pencil and no double-click editing (the container withholds it while a Re-load or a
   * step save is in flight, so a rename can't race them). Resolve on success (the caller swaps the
   * renamed flow into `flows`); throw an Error to keep the editor open — its message is shown under
   * the field. */
  onRenameFlow?: (flowId: string, newName: string) => Promise<void>;
}

// Explains the Stale badge's actual cause (any successful save on ANY flow in this channel
// rewrites the whole channel's business-rules text, which is what this flow's own diagram was
// generated from) and the fix (Re-load), not just that it's stale — the plain one-line tooltip
// this replaced said WHAT but not WHY or HOW clearly enough for an admin unfamiliar with the
// underlying per-channel (not per-flow) text model.
const STALE_BADGE_TOOLTIP = (
  <>
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
      Stale — rules changed elsewhere
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
      This flow&apos;s diagram was generated from an older version of this channel&apos;s business
      rules text. That text has since changed — usually from saving an edit on another flow in
      this same channel, or updating Channel Config directly.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
      Click &quot;Re-load&quot; above to regenerate every flow from the current text and clear
      this badge.
    </Typography>
  </>
);

// The backend keeps no "named by a person" marker (shay-email-backend#564), so a re-parse — Re-load, or
// any business-rules change that makes flows stale — regenerates the AI names. Said here, where the
// admin renames, so a custom name that later disappears isn't mistaken for a bug.
const RENAME_HINT = 'Custom names are reset when the business rules are re-parsed.';

const RENAME_TOOLTIP = (
  <>
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
      Rename flow
    </Typography>
    <Typography variant="caption" sx={{ display: 'block' }}>
      Custom names are reset when the business rules are re-parsed (Re-load, or after a rules change).
    </Typography>
  </>
);

// Safari reports the Enter that merely CONFIRMS an IME composition as a plain keydown that arrives right
// AFTER compositionend (isComposing is already false, keyCode 229), so an Enter this soon after a
// composition ended is treated as part of it. Tracking composition events replaces sniffing keyCode 229,
// which some Android soft keyboards also send for a real Enter.
const COMPOSITION_END_GRACE_MS = 50;

/**
 * Business_rule_canvas branch (PRISM7_CHANNEL_RULES_CANVAS_TDD.md §8.2) — the persistent left
 * rail: a compact list (not a card grid — TDD §8.1 explicitly rules that out as an extra
 * click-through step), one row per flow, with a stale badge sourced from the list response's
 * own `is_stale` field. Selecting a row swaps the center canvas in place; the rail itself never
 * navigates away. The manual "Re-load" action lives in RuleFlowCanvas's own header (right-aligned
 * next to the flow name), not here — it's a channel-level action, but visually anchored to
 * whichever flow is currently open.
 *
 * Flow rename (shay-email-backend#564): when `onRenameFlow` is passed, each row's name can be edited
 * inline — double-click the name or use the pencil (hover / keyboard focus). Enter or clicking away
 * saves, Esc cancels. The editor never selects the row, and only the row being saved is disabled.
 */
const RuleFlowRail: React.FC<RuleFlowRailProps> = ({
  flows,
  selectedFlowId,
  onSelectFlow,
  onRenameFlow,
}) => {
  // Inline rename: at most one row has an editor open, but a save started from one row can still
  // be in flight when the admin opens another — so which row is EDITING and which is SAVING are
  // tracked separately, and every async result is checked against the row it belongs to.
  const [editingFlowId, setEditingFlowId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [savingFlowId, setSavingFlowId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectOnOpenRef = useRef(false);
  // Enter, Esc and a successful save all END the edit, and the input they unmount (or disable)
  // can fire a blur right after — which must not save a second time or resurrect a cancelled edit.
  const settledRef = useRef(true);
  // Mirrors editingFlowId for the async save below, which would otherwise read a stale closure.
  const editingFlowIdRef = useRef<string | null>(null);
  // The name the server just rejected. Clicking away must not re-send it (that would re-fire the
  // same failing PATCH on every click-away); pressing Enter is the explicit retry.
  const failedNameRef = useRef<string | null>(null);
  // Set only when a save that came from Enter fails: the admin is still in the field, so put the
  // caret back. A failure that came from clicking away must NOT pull focus back from wherever the
  // admin went (e.g. a Label field on the canvas).
  const refocusRef = useRef(false);
  // The row whose pencil gets focus once its editor has been closed from the KEYBOARD (Enter / Esc) —
  // otherwise focus drops to <body> and a keyboard user loses their place in the list. Never set when
  // the admin clicked away: focus is wherever they went.
  const restoreFocusRef = useRef<string | null>(null);
  const pencilRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // The row whose pencil we are about to focus ourselves. While set, that pencil's Tooltip ignores
  // FOCUS (hover still works): a programmatic focus right after Enter / Esc counts as keyboard focus
  // and would otherwise pop the "reset on re-parse" tooltip open every time an edit ends.
  const [quietFocusFlowId, setQuietFocusFlowId] = useState<string | null>(null);
  // IME composition state for the editor's Enter / Esc handling (see COMPOSITION_END_GRACE_MS).
  const composingRef = useRef(false);
  const compositionEndedAtRef = useRef(0);

  const startEdit = (flow: RuleFlow) => {
    settledRef.current = false;
    selectOnOpenRef.current = true;
    refocusRef.current = false;
    failedNameRef.current = null;
    editingFlowIdRef.current = flow.id;
    setDraft(flow.flow_name);
    setEditError(null);
    setEditingFlowId(flow.id);
  };

  const cancelEdit = () => {
    settledRef.current = true;
    failedNameRef.current = null;
    editingFlowIdRef.current = null;
    setEditingFlowId(null);
    setEditError(null);
  };

  // Focus (and select all) when an editor opens; after a failed Enter-save re-enables the field,
  // put the caret back so the admin can fix the name without clicking into it again.
  useEffect(() => {
    if (editingFlowId === null || savingFlowId === editingFlowId) return;
    const input = inputRef.current;
    if (!input) return;
    if (selectOnOpenRef.current) {
      input.focus();
      input.select();
      selectOnOpenRef.current = false;
    } else if (refocusRef.current) {
      input.focus();
      refocusRef.current = false;
    }
  }, [editingFlowId, savingFlowId]);

  // A re-parse / reload can drop the row being edited out from under the editor.
  useEffect(() => {
    if (editingFlowId !== null && !flows.some((f) => f.id === editingFlowId)) cancelEdit();
  }, [flows, editingFlowId]);

  // Hand the row whose editor Enter / Esc just closed over to the focus effect below. Going through
  // state (instead of focusing right here) lets that row's Tooltip re-render with its focus listener
  // off FIRST, so the focus we move doesn't open it.
  useEffect(() => {
    if (editingFlowId !== null || restoreFocusRef.current === null) return;
    setQuietFocusFlowId(restoreFocusRef.current);
    restoreFocusRef.current = null;
  }, [editingFlowId]);

  // Return focus to that pencil. The Tooltip reacts to focus synchronously inside focus(), by which
  // time its focus listener is already off; clear the flag straight after so it is a one-shot (left
  // set, the next keyboard close would set it to the same value and this effect would never re-run).
  // If the pencil isn't on screen (e.g. rename was withdrawn meanwhile) there is simply nothing to focus.
  useEffect(() => {
    if (quietFocusFlowId === null) return;
    // Only if focus has nowhere else to be: a slow Enter save can finish after the admin has already
    // clicked into another field (e.g. a Label on the canvas), and must not drag them back here.
    const active = document.activeElement;
    if (!active || active === document.body) pencilRefs.current[quietFocusFlowId]?.focus();
    setQuietFocusFlowId(null);
  }, [quietFocusFlowId]);

  // The container withholds onRenameFlow while a Re-load or a step save runs. An editor left open
  // would be dead (Enter and click-away silently do nothing), so close it — but not mid-save, where
  // the in-flight request still needs its row, and not while a message is on screen: a save that
  // FAILED after rename was withdrawn must still show its error (closing would clear it, and the
  // admin would never learn the rename didn't happen). Re-runs once either condition clears.
  useEffect(() => {
    if (!onRenameFlow && editingFlowId !== null && savingFlowId !== editingFlowId && !editError) cancelEdit();
  }, [onRenameFlow, editingFlowId, savingFlowId, editError]);

  /**
   * Validates the draft and saves it through `onRenameFlow`.
   *
   * `source` says what triggered the save: Enter is an explicit (re)try, while a blur must not
   * re-send a name the server just rejected. Does nothing if the edit is already settled, the name
   * is unchanged (closes the editor, no request), or it is empty / over the limit (message shown
   * under the field, no request). A failure keeps the editor open with the server's message.
   */
  const commitEdit = async (flow: RuleFlow, source: 'enter' | 'blur') => {
    if (settledRef.current || !onRenameFlow) return;
    // Same NFC + trim + whitespace collapse BE applies, and the same character count, so "unchanged"
    // and the length check agree with what would actually be stored.
    const next = normalizeFlowName(draft);
    if (next === flow.flow_name) {
      if (source === 'enter') restoreFocusRef.current = flow.id;
      cancelEdit();
      return;
    }
    if (!next) {
      setEditError('Flow name cannot be empty.');
      return;
    }
    if (flowNameLength(next) > FLOW_NAME_MAX_LENGTH) {
      setEditError(`Flow name must be ${FLOW_NAME_MAX_LENGTH} characters or fewer.`);
      return;
    }
    if (source === 'blur' && failedNameRef.current === next) return;
    settledRef.current = true;
    setSavingFlowId(flow.id);
    setEditError(null);
    try {
      await onRenameFlow(flow.id, next);
      // The admin may have opened ANOTHER row's editor while this save was in flight; its result
      // must neither close that editor nor paint an error under it.
      if (editingFlowIdRef.current === flow.id) {
        if (source === 'enter') restoreFocusRef.current = flow.id;
        editingFlowIdRef.current = null;
        setEditingFlowId(null);
      }
    } catch (err) {
      if (editingFlowIdRef.current === flow.id) {
        settledRef.current = false;
        failedNameRef.current = next;
        refocusRef.current = source === 'enter';
        setEditError(err instanceof Error ? err.message : 'Could not rename this flow. Please try again.');
      }
    } finally {
      setSavingFlowId((current) => (current === flow.id ? null : current));
    }
  };

  const stopClick = (event: React.SyntheticEvent) => event.stopPropagation();

  return (
    <Box sx={{ width: 260, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
      <List dense sx={{ overflow: 'auto', flex: 1 }}>
        {flows.map((flow) => (
          <ListItemButton
            key={flow.id}
            selected={flow.id === selectedFlowId}
            onClick={() => onSelectFlow(flow.id)}
            // Badge/heading overlap fix — ListItemButton's default flex-row put the Stale/
            // Incomplete/Recommendations chips as siblings of ListItemText in the SAME row, so a
            // long flow_name wrapping onto a second line visually overlapped the chips squeezed
            // beside its first line. Switching to a column with the chips in their own row below
            // the heading removes the overlap regardless of how long the name or how many chips.
            sx={{ flexDirection: 'column', alignItems: 'stretch' }}
          >
            {editingFlowId === flow.id ? (
              // Click/double-click inside the editor must never reach the row's onClick above, or
              // typing in the field would re-select the row / switch the open flow.
              <Box onClick={stopClick} onDoubleClick={stopClick} sx={{ width: '100%' }}>
                <TextField
                  id={`flow-name-${flow.id}`}
                  size="small"
                  fullWidth
                  value={draft}
                  disabled={savingFlowId === flow.id}
                  error={Boolean(editError)}
                  // The standing hint is shown while editing too: most admins rename by double-click and
                  // never see the pencil's tooltip, so this is where the re-parse warning has to be.
                  helperText={editError ?? RENAME_HINT}
                  inputRef={inputRef}
                  onChange={(event) => {
                    setDraft(event.target.value);
                    if (editError) setEditError(null);
                  }}
                  onKeyDown={(event) => {
                    // Enter/Esc are consumed here: Esc would otherwise bubble up and close the whole
                    // Settings drawer, and Enter must not also activate the row button around us.
                    const composing =
                      event.nativeEvent.isComposing ||
                      composingRef.current ||
                      Date.now() - compositionEndedAtRef.current < COMPOSITION_END_GRACE_MS;
                    if (event.key === 'Enter' && !composing) {
                      event.preventDefault();
                      event.stopPropagation();
                      void commitEdit(flow, 'enter');
                    } else if (event.key === 'Escape') {
                      // Esc while composing only cancels the composition — the edit stays — but it must
                      // not reach the drawer behind us either way.
                      event.stopPropagation();
                      if (!composing) {
                        event.preventDefault();
                        restoreFocusRef.current = flow.id;
                        cancelEdit();
                      }
                    }
                  }}
                  onBlur={() => {
                    // Alt-tabbing away (e.g. to copy the right name) blurs the field too, but the admin
                    // hasn't finished — don't persist a half-typed name.
                    if (!document.hasFocus()) return;
                    void commitEdit(flow, 'blur');
                  }}
                  slotProps={{
                    htmlInput: {
                      'aria-label': 'Flow name',
                      onCompositionStart: () => {
                        composingRef.current = true;
                      },
                      onCompositionEnd: () => {
                        composingRef.current = false;
                        compositionEndedAtRef.current = Date.now();
                      },
                    },
                    // Only a real message is an alert; the standing hint must not be announced as one.
                    formHelperText: { role: editError ? 'alert' : undefined },
                  }}
                  sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.875rem' } }}
                />
              </Box>
            ) : (
              <ListItemText
                primary={
                  onRenameFlow ? (
                    // Double-click the name, or use the pencil (hover / keyboard focus) — double-click
                    // alone isn't keyboard-reachable. Both stop propagation so starting an edit
                    // doesn't also run the row's select handler a second time.
                    <Box component="span" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <Box
                        component="span"
                        title="Double-click to rename"
                        onDoubleClick={(event) => {
                          event.stopPropagation();
                          startEdit(flow);
                        }}
                        sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                      >
                        {flow.flow_name}
                      </Box>
                      <Tooltip title={RENAME_TOOLTIP} disableFocusListener={quietFocusFlowId === flow.id}>
                        <IconButton
                          ref={(element) => {
                            pencilRefs.current[flow.id] = element;
                          }}
                          size="small"
                          aria-label={`Rename flow ${flow.flow_name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            startEdit(flow);
                          }}
                          onDoubleClick={stopClick}
                          sx={{
                            p: 0.25,
                            flexShrink: 0,
                            opacity: 0,
                            '&:focus-visible': { opacity: 1 },
                            '.MuiListItemButton-root:hover &': { opacity: 1 },
                            // Touch screens have no hover: keep the pencil visible so it can be found
                            // (and a tap near it isn't an invisible, accidental edit).
                            '@media (hover: none)': { opacity: 1 },
                          }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    flow.flow_name
                  )
                }
                secondary={flow.trigger_condition_text}
                secondaryTypographyProps={{ noWrap: true }}
                sx={{ width: '100%', m: 0 }}
              />
            )}
            {/* business_rules_workflow_count — the per-row run-count badge that used to render here
                was removed per admin feedback (too much visual weight repeated on every row). The
                count is now shown once, compactly, in the canvas header for whichever flow is
                selected (RuleFlowCanvas's "Runs: N" label), plus the full breakdown by flow in the
                Channel Operations Dashboard — no need to repeat it a third time per row here. */}
            {(flow.is_stale || isFlowIncomplete(flow) || isFlowMissingDetail(flow)) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, width: '100%' }}>
                {flow.is_stale && (
                  <Tooltip title={STALE_BADGE_TOOLTIP}>
                    <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="Stale" />
                  </Tooltip>
                )}
                {/* ADR §3 — hard-blocks a flow as incomplete when one of its ACTIONs requires
                    approval but has no resolved recipient. Independent of the Stale badge above —
                    both can show at once (e.g. a stale, incomplete flow), and neither depends on
                    the other's fix. */}
                {isFlowIncomplete(flow) && (
                  <Tooltip title="An approval step in this flow has no resolved recipient — open it and assign one">
                    <Chip size="small" color="error" icon={<ErrorOutlineIcon />} label="Incomplete" />
                  </Tooltip>
                )}
                {/* Fix 14/Item 7 — non-blocking, independent of the two chips above: severity-
                    ranked (Stale blocks editing, Incomplete blocks flow completeness, this blocks
                    nothing), all three can render together. */}
                {isFlowMissingDetail(flow) && (
                  <Tooltip title="This flow has steps that could use more detail">
                    <Chip size="small" color="info" icon={<InfoOutlinedIcon />} label="Recommendations" />
                  </Tooltip>
                )}
              </Box>
            )}
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default RuleFlowRail;
